import {
  BUYBACK_FIELD,
  BuybackFee,
  BuybackPayoutInput,
} from '../../common/buybackTypes'

export const formatNumber = (
  value?: number,
  decimalOrSymbol?: number | string,
  maybeSymbol?: string
) => {
  const decimal = typeof decimalOrSymbol === 'number' ? decimalOrSymbol : 0
  const symbol =
    (typeof decimalOrSymbol === 'string' ? decimalOrSymbol : maybeSymbol) || ''
  const safe = Math.max(0, Number(value) || 0)

  return `${safe.toLocaleString('cs-CZ', {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal,
  })}${symbol}`
}

export const inputNumberFormatter = (value?: number) =>
  formatNumber(Number(value))

export const computeMinimumRent = (fees: BuybackFee[] = []): number =>
  Math.max(
    0,
    Math.round(
      (fees || []).reduce(
        (sum, fee) => sum + Math.max(0, Number(fee?.amount) || 0),
        0
      )
    )
  )

export const computeProcessCostsTotal = (fees: BuybackFee[] = []): number =>
  Math.max(
    0,
    Math.round(
      (fees || []).reduce(
        (sum, fee) => sum + Math.max(0, Number(fee?.amount) || 0),
        0
      )
    )
  )

/**
 * Compute the payout amount (final cash flow to the seller).
 *  - OfferPrice - (Commission + DebtFees + ProcessCosts + DebtAmounts)
 *
 * Edge cases:
 * - Missing numeric fields are treated as 0.
 * - Negative intermediate values are prevented by using fallback 0; result itself can be negative.
 *
 * @param BuybackPayoutInput subset of `BuybackFormValues`
 * @returns Payout amount (can be negative).
 * @description OfferPrice - (Commission + DebtFees + ProcessCosts + DebtAmounts)
 */
export const computePayoutAmount = (formValues: BuybackPayoutInput): number => {
  const commission =
    typeof formValues[BUYBACK_FIELD.CommissionAmount] === 'number'
      ? formValues[BUYBACK_FIELD.CommissionAmount]
      : formValues[BUYBACK_FIELD.BaseCommissionAmount] ?? 0

  const debtsTotal = formValues[BUYBACK_FIELD.DebtAmountTotal] ?? 0
  const debtFees =
    typeof formValues[BUYBACK_FIELD.DebtFeeTotal] === 'number'
      ? formValues[BUYBACK_FIELD.DebtFeeTotal]
      : formValues[BUYBACK_FIELD.BaseDebtFeeTotal] ?? 0
  const processCosts = formValues[BUYBACK_FIELD.ProcessCostAmountTotal] ?? 0
  const offerPrice = formValues[BUYBACK_FIELD.OfferPrice] ?? 0

  const totalExpenses = commission + debtFees + processCosts + debtsTotal

  return offerPrice - totalExpenses
}

export const computeBaseBuybackPrice = (
  propertyPrice: number,
  annualInterestRate: number,
  rentalDurationMonths: number
): number => {
  const principal = Math.max(0, Number(propertyPrice) || 0)
  const rate = Math.max(0, Number(annualInterestRate) || 0) / 100
  const months = Math.max(0, Number(rentalDurationMonths) || 0)

  return Math.round(principal + principal * rate * (months / 12))
}

export const computeDebtFees = (
  debts: Array<{ amount?: number; secured?: boolean }> = []
): number[] => {
  let unsecuredOrder = 0
  return debts.map(debt => {
    const secured = !!debt?.secured
    if (secured) return getSecuredDebtFee(debt?.amount || 0)
    unsecuredOrder += 1

    return getUnsecuredDebtFeeByOrder(unsecuredOrder)
  })
}

/**
 * Safely extract number from object key, return default if not found or invalid.
 * @param obj Object to extract from
 * @param key Key to extract
 * @param def Default value if key not found or invalid
 * @returns Extracted number or default
 */
export const extractNumberFromObject = (
  obj: Record<string, unknown>,
  key: string,
  def = 0
): number => {
  const value = obj[key]
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : def
  }
  const number = Number(value)
  return Number.isFinite(number) ? number : def
}

/**
 * Fee for secured debt by its amount.
 */
export const getSecuredDebtFee = (amount = 0): number => {
  const debt = Math.max(0, Number(amount) || 0)
  if (debt <= 500_000) return 30_000
  if (debt <= 3_000_000) return 50_000
  if (debt <= 6_000_000) return 100_000
  if (debt <= 9_000_000) return 150_000
  if (debt <= 12_000_000) return 200_000
  if (debt <= 15_000_000) return 250_000
  if (debt <= 18_000_000) return 300_000
  if (debt <= 21_000_000) return 350_000
  if (debt <= 24_000_000) return 400_000
  if (debt <= 27_000_000) return 450_000
  return 500_000
}

/**
 * Fee per unsecured debt by its order (1st, 2nd, 3rd, ...)
 *    1:   15 000 Kč
 *  2–5:   13 000 Kč
 * 6–10:   11 000 Kč
 *  11+:   10 000 Kč
 */
export const getUnsecuredDebtFeeByOrder = (order = 1): number => {
  const n = Math.max(1, Math.floor(Number(order) || 1))
  if (n === 1) return 15_000
  if (n <= 5) return 13_000
  if (n <= 10) return 11_000
  return 10_000
}

/**
 * 50K per every 3M
 */
export const getCommissionAmount = (offerPrice = 0): number => {
  const offer = Math.max(0, Number(offerPrice) || 0)
  return Math.ceil(offer / 3000000) * 50000
}
